import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AccountMarketplace, AccountState, AccountSubType, AccountType, Marketplace } from "@front/m19-api-client";
import { AccountGroup, AccountMarketplaceEx, OrganizationAccountGroups, organizationCompare } from "@front/m19-models";
import {
  AccountMarketplaceService,
  AuthService,
  isSuspendedForAdmin,
  OrganizationAccountGroupService,
  OrganizationService,
  SellingPartnerService,
} from "@front/m19-services";
import { ModalService, ToasterService } from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import {
  GrantOrganicDataAccessModalComponent,
  GrantOrganicDataAccessModalData,
} from "@m19-board/shared/grant-organic-data-access-modal/grant-organic-data-access-modal.component";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { forkJoin, map, switchMap, take } from "rxjs";

export enum AccountBidderStatus {
  FULL_AUTOMATION = "FULL_AUTOMATION",
  FULL_AUTOMATION_WITH_DSP = "FULL_AUTOMATION_WITH_DSP",
  MANUAL_SETUP_REQUIRED = "MANUAL_SETUP_REQUIRED",
  PAYMENT_ISSUE = "PAYMENT_ISSUE",
  ADVERTISING_ACCESS_LOST = "ADVERTISING_ACCESS_LOST",
  PULL_STATS = "PULL_STATS",
}

@Injectable({ providedIn: "root" })
export class AccountSettingsV2Service {
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly layoutSelectorService = inject(LayoutSelectorService);
  private readonly authService = inject(AuthService);
  private readonly toasterService = inject(ToasterService);
  private readonly sellingPartnerService = inject(SellingPartnerService);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly organizationService = inject(OrganizationService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly user$ = this.authService.loggedUser$;

  public readonly organizationsToDisplay$ = this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
    map((orgs) =>
      orgs?.filter(
        (o) =>
          isSuspendedForAdmin(o) || (o.accountGroups?.length && o.accountGroups.some((g) => g.resources.length > 0)),
      ),
    ),
    map((orgs) => orgs?.sort(organizationCompare)),
  );

  public readonly customCampaignNameAllowed$ = this.user$.pipe(map((user) => user?.allowCustomCampaignName ?? false));

  public readonly canLoginWithAmazonOrgIds$ = this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
    map((organizations) => {
      // canLoginWithAmazon is overriden for whitelabel parent ppc board organization
      const canLoginWithAmazonGlobal =
        this.layoutSelectorService.getBoardType() === BoardType.M19 ||
        this.layoutSelectorService.getBoardType() === BoardType.WHITELABEL_SELFSERVICE;
      const canLoginWithAmazon = new Map<number, boolean>();
      organizations?.forEach((o) => {
        if (o.isParentPpcBoard) {
          canLoginWithAmazon.set(o.id, true);
        } else {
          canLoginWithAmazon.set(o.id, canLoginWithAmazonGlobal);
        }
      });
      return canLoginWithAmazon;
    }),
  );

  public openGrantSellingPartnerAccessModal(accountMarketplaces: AccountMarketplace[]) {
    if (accountMarketplaces.length === 0) {
      return;
    }
    const data: GrantOrganicDataAccessModalData = { accountMarketplaces };
    this.modalService
      .openModal<GrantOrganicDataAccessModalData, AccountMarketplace>(GrantOrganicDataAccessModalComponent, {
        modalTitle: this.translocoService.translate("default-layout.grant_access"),
        data,
      })
      .afterClosed()
      .subscribe((am) => {
        this.grantSellingPartnerAccess(
          am.accountId,
          am.marketplace,
          am.accountType!,
          am.accountSubType === AccountSubType.KDP_AUTHOR,
        );
      });
  }

  public grantSellingPartnerAccess(
    accountId: string,
    marketplace: Marketplace,
    accountType: AccountType,
    isKDP: boolean,
  ) {
    if (isKDP) {
      this.toasterService.warning(
        "Organic data API access is not yet supported for KDP accounts",
        "Selling partner API access warning",
      );
      return;
    }
    this.sellingPartnerService
      .requestSellingPartnerAccess(accountId, marketplace, accountType === AccountType.VENDOR, isKDP)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (id: string) => {
          this.toasterService.success(
            "API access successfully granted for account " + id,
            "Selling partner API access validated",
          );
        },
        error: (err: string) => {
          this.toasterService.error(err, "Selling partner API access error");
        },
      });
  }

  public moveMultipleAccountsToGroup(accountMarketplaces: AccountMarketplace[], accountGroup: AccountGroup) {
    return forkJoin(accountMarketplaces.map((am) => this.moveSingleAccountToGroup(am, accountGroup)));
  }

  public moveSingleAccountToGroup(accountMarketplace: AccountMarketplace, accountGroup: AccountGroup) {
    return this.accountMarketplaceService.moveAccountToGroup(accountMarketplace, accountGroup.id, accountGroup.name);
  }

  public deleteSingleAccount(accountMarketplace: AccountMarketplace) {
    return this.accountMarketplaceService.removeAccountMarketplace(accountMarketplace).pipe(catchAjaxError());
  }

  public deleteMultipleAccounts(accountMarketplaces: AccountMarketplace[]) {
    return forkJoin(accountMarketplaces.map((am) => this.deleteSingleAccount(am)));
  }

  public getAccountBidderStatus(accountMarketplace: AccountMarketplaceEx): AccountBidderStatus {
    if (!accountMarketplace.hasAccessToAdvertising) {
      return AccountBidderStatus.ADVERTISING_ACCESS_LOST;
    }
    if (accountMarketplace.state === AccountState.BIDDER_ON) {
      if (
        accountMarketplace.dspAdvertiser?.bidderOrganizationId &&
        accountMarketplace.dspAdvertiser?.bidderOrganizationId === accountMarketplace.resourceOrganizationId
      ) {
        return AccountBidderStatus.FULL_AUTOMATION_WITH_DSP;
      }
      return AccountBidderStatus.FULL_AUTOMATION;
    }
    if (accountMarketplace.state === AccountState.PAYMENT_ISSUE) {
      return AccountBidderStatus.PAYMENT_ISSUE;
    }
    if (!accountMarketplace.activated) {
      return AccountBidderStatus.MANUAL_SETUP_REQUIRED;
    }
    return AccountBidderStatus.PULL_STATS;
  }

  public openEditDefaultCampaignLabelModal(organization: OrganizationAccountGroups) {
    const data: InputModalData = {
      title: this.translocoService.translate("account-setting.edit_default_campaign_label"),
      initialValue: organization.organization.defaultCampaignLabel ?? "",
      maxLength: 10,
    };

    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("account-setting.edit_default_campaign_label"),
      data,
    });
    ref
      .afterClosed()
      .pipe(
        map((newName: string) => newName.trim()),
        switchMap((newName: string) => this.organizationService.updateDefaultCampaignLabel(organization.id, newName)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success("Default campaign label updated");
        },
        error: (error: string) => this.toasterService.error(error, "Campaign label update error"),
      });
  }
}
